import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE_URL, normalizeUserEmail, isAuthorizedGoogleAccount } from '../utils/config';
import { initializeUserData } from '../utils/lambdaApi';

const AuthContext = createContext();

function mapToCurrentUser(profile) {
  if (!profile) return null;
  const email = normalizeUserEmail(profile.email);
  return {
    uid: profile.sub || email,
    email,
    displayName: profile.name || profile.displayName || email,
    photoURL: profile.picture || profile.photoURL || null,
  };
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialAuth, setInitialAuth] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authType, setAuthType] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [userData, setUserData] = useState(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  const fetchUserData = useCallback(async (user) => {
    if (!user?.email) return;

    const storedUserData = localStorage.getItem(`userData_${user.uid || user.email}`);
    if (storedUserData) {
      setUserData(JSON.parse(storedUserData));
      return;
    }

    setApiLoading(true);
    setApiError(null);

    try {
      const initialData = await initializeUserData(user.email, null);

      if (initialData.userData) {
        setUserData(initialData.userData);
        localStorage.setItem(
          `userData_${user.uid || user.email}`,
          JSON.stringify(initialData.userData)
        );

        if (initialData.userData.department) {
          const departmentCodeMatch = initialData.userData.department?.match(/^(\d+)/);
          const userDepartmentCode = departmentCodeMatch
            ? departmentCodeMatch[1]
            : initialData.userData.department;

          if (userDepartmentCode) {
            await initializeUserData(user.email, userDepartmentCode);
          }
        }
      }
    } catch (err) {
      setApiError(err.message);
    } finally {
      setApiLoading(false);
    }
  }, []);

  const unifiedBackendLogin = async (token, profile) => {
    const resolvedAuthType =
      (profile?.email && isAuthorizedGoogleAccount(profile.hd, profile.email)) ||
      profile?.auth_type === 'google'
        ? 'google'
        : 'local';

    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        authType: resolvedAuthType,
        token,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Error al establecer sesión');
    }

    return response.json();
  };

  const initializeAuth = useCallback(async () => {
    try {
      let effectiveToken = null;
      try {
        effectiveToken = sessionStorage.getItem('session_token');
      } catch (storageError) {
        console.warn('No se pudo leer session_token:', storageError);
      }

      if (effectiveToken) {
        setSessionToken(effectiveToken);
      }

      if (!effectiveToken) {
        setIsAuthenticated(false);
        setCurrentUser(null);
        setInitialAuth(false);
        setLoading(false);
        return;
      }

      const headers = { 'Content-Type': 'application/json' };
      if (effectiveToken) {
        headers.Authorization = `Bearer ${effectiveToken}`;
      }

      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        credentials: 'include',
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        const profile = {
          email: data.user.email,
          name: data.user.name,
          picture: data.user.picture,
          auth_type: data.user.auth_type,
        };

        setUserProfile(profile);
        setCurrentUser(mapToCurrentUser(profile));
        setAuthType(data.user.auth_type || 'google');
        setIsAuthenticated(true);

        if (data.token) {
          setSessionToken(data.token);
          sessionStorage.setItem('session_token', data.token);
        }

        await fetchUserData(mapToCurrentUser(profile));
      } else if (response.status === 401) {
        setIsAuthenticated(false);
        setCurrentUser(null);
        setUserProfile(null);
        setSessionToken(null);
        sessionStorage.removeItem('session_token');
      }
    } catch (error) {
      console.error('Error en initializeAuth:', error);
      setIsAuthenticated(false);
      setCurrentUser(null);
    } finally {
      setInitialAuth(false);
      setLoading(false);
    }
  }, [fetchUserData]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const login = async (token, profile) => {
    if (!isAuthorizedGoogleAccount(profile.hd, profile.email)) {
      throw new Error('Solo se permiten cuentas de @xinerlink.cl o @valorate.cl');
    }

    const sessionData = await unifiedBackendLogin(token, profile);
    const sessionUser = sessionData.user || profile;
    const mappedUser = mapToCurrentUser({
      email: sessionUser.email || profile.email,
      name: sessionUser.name || profile.name,
      picture: profile.picture,
      sub: profile.sub,
    });

    setUserProfile(sessionUser);
    setCurrentUser(mappedUser);
    setIsAuthenticated(true);
    setAuthType('google');

    if (sessionData.token) {
      setSessionToken(sessionData.token);
      sessionStorage.setItem('session_token', sessionData.token);
    }

    localStorage.setItem('userBasicInfo', JSON.stringify(mappedUser));
    sessionStorage.setItem('justLoggedIn', 'true');
    sessionStorage.setItem('refreshTasksOnLogin', 'true');
    setInitialAuth(false);

    await fetchUserData(mappedUser);
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.warn('Error limpiando sesión en backend:', error);
    }

    if (currentUser) {
      localStorage.removeItem(`userData_${currentUser.uid || currentUser.email}`);
    }

    localStorage.removeItem('userBasicInfo');
    sessionStorage.removeItem('session_token');
    sessionStorage.removeItem('justLoggedIn');
    sessionStorage.removeItem('refreshTasksOnLogin');

    setCurrentUser(null);
    setUserProfile(null);
    setUserData(null);
    setIsAuthenticated(false);
    setAuthType(null);
    setSessionToken(null);
    setApiError(null);
  };

  const value = useMemo(
    () => ({
      currentUser,
      userProfile,
      initialAuth,
      isAuthenticated,
      authType,
      sessionToken,
      userData,
      apiLoading,
      apiError,
      loading,
      login,
      logout,
      initializeAuth,
      fetchUserData,
    }),
    [
      currentUser,
      userProfile,
      initialAuth,
      isAuthenticated,
      authType,
      sessionToken,
      userData,
      apiLoading,
      apiError,
      loading,
      fetchUserData,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
